// BuildupForm.jsx
import { useState, useEffect } from "react"
import { UniversalDropDown } from "@fileops/swmm-react"
import { Button } from '@mui/material'
import "../../css/DemoCode.css"

/**
 * Dry Weather Form is a group of data inputs usually shown when clicking on Node->Inflows. 
 * This form is contained within InflowsDialog.
 * 
 * @param {JSON} model A SWMM model in JSON format. In most cases this
 * is a 'submodel' containing only a copy of the affected sections. Using a copy allows
 * for continuous react-style editing without forcing the model to run on every keystroke.
 * @param {function} setModel Update function for updating 'model'.
 * @param {JSON} fullModel The 'full' SWMM model that contains supporting data for this component.
 * @param {string} target The name of the node targetted by this form.
 * @returns UI for interactive swmm modeling.
 */
export default function BuildupForm({ fullModel, setFullModel, target}) {
  const [constituent, setConstituent]    = useState('*')
  const [constituents]  = useState(Object.keys(fullModel.POLLUTANTS).length===0?[]:Object.keys(fullModel.POLLUTANTS))
  const [timeSeries_s]  = useState(Object.keys(fullModel.TIMESERIES).length===0?[]:Object.keys(fullModel.TIMESERIES))
  const [func, setFunc]                  = useState('NONE')
  const [funcs]                = useState(['NONE', 'POW', 'EXP', 'SAT', 'EXT'])
  const [normalizer, setNormalizer]      = useState('AREA')
  const [normalizers]    = useState(['AREA', 'CURB'])
  const [coeff1, setCoeff1]              = useState(0)
  const [coeff2, setCoeff2]              = useState(0)
  const [coeff3, setCoeff3]              = useState(0)
  const [scaleFactor, setScaleFactor]    = useState(0)
  const [timeSeries, setTimeSeries]      = useState('*')

  // If a constituent has been selected but the fullModel does not contain a BUILDUP for 
  // the constituent, then apply default values. Otherwise, use the fullModel BUILDUP values.
  useEffect(()=>{
    if(constituent !== '*'){
      if(fullModel.BUILDUP[target] && fullModel.BUILDUP[target][constituent]){
        setFunc(fullModel.BUILDUP[target][constituent].Function)
        setNormalizer(fullModel.BUILDUP[target][constituent].Normalizer)
        setCoeff1(fullModel.BUILDUP[target][constituent].Coeff1)
        setCoeff2(fullModel.BUILDUP[target][constituent].Coeff2)
        setCoeff3(fullModel.BUILDUP[target][constituent].Coeff3)
        setScaleFactor(fullModel.BUILDUP[target][constituent].ScaleFactor)
        setTimeSeries(fullModel.BUILDUP[target][constituent].TimeSeries)
      } else {
        setFunc('NONE')
        setNormalizer('AREA')
        setCoeff1(0)
        setCoeff2(0)
        setCoeff3(0)
        setScaleFactor(0)
        setTimeSeries('')
      }
    }

  }, [constituent])

  const saveEdits = () => {
    if(constituent !== '*'){
      // If this is an external time series based function, use timeSeries and scaleFactor
      if(func === 'EXT'){
        setFullModel(prev => ({
          ...prev, 
          BUILDUP: {
            ...prev.BUILDUP, 
            [target]: {
              ...prev.BUILDUP[target],
              [constituent]: {
                Coeff1: coeff1,
                ScaleFactor: scaleFactor,
                TimeSeries: timeSeries,
                Function: func,
                Normalizer: normalizer
              }
            }
          }
        }))
      }
      // otherwise, use coeff2 and coeff3
      else {
        setFullModel(prev => ({
          ...prev, 
          BUILDUP: {
            ...prev.BUILDUP, 
            [target]: {
              ...prev.BUILDUP[target],
              [constituent]: {
                Coeff1: coeff1,
                Coeff2: coeff2,
                Coeff3: coeff3,
                Function: func,
                Normalizer: normalizer
              }
            }
          }
        }))
      }
    }
  }

  return(
    <>
    {
      <>
        <div>
          <Button fullWidth onClick={saveEdits} >Save Edits</Button>
        </div>
        {(
          target && 
          target.length > 0 &&
          <label className='editor-small' >Constituent
            <UniversalDropDown style={{padding: '0'}}
              IDs={constituents} 
              selectText={"Select a Constituent"} 
              value={constituent}
              onChange={(val)=>{setConstituent(val)}} >
            </UniversalDropDown>
          </label>
        )}
        {(
          target && 
          target.length > 0 &&
          <label className='editor-small' >Function
            <UniversalDropDown style={{padding: '0'}}
              IDs={funcs} 
              selectText={"Select a Function"} 
              value={func}
              onChange={(val)=>{setFunc(val)}} >
            </UniversalDropDown>
          </label>
        )}
        {(
          target && 
          target.length > 0 &&
          <label className='editor-small' >Normalizer
            <UniversalDropDown style={{padding: '0'}}
              IDs={normalizers} 
              selectText={"Select a Normalizer"} 
              value={normalizer}
              onChange={(val)=>{setNormalizer(val)}} >
            </UniversalDropDown>
          </label>
        )}
        {(
          target && 
          target.length > 0 &&
          <label className='editor-small' >Max buildup
            <input  type = "text"
                    style={{width: '95%', display: 'flex'}}
                    onChange={(e)=>{setCoeff1(e.target.value)}}
                    value={coeff1}
                    id={'coeff1_'+target}
            />
          </label>
        )}
        {(
          target && 
          target.length > 0 &&
          func !== 'EXT' &&
          <label className='editor-small' >Rate constant
            <input  type = "text"
                    style={{width: '95%', display: 'flex'}}
                    onChange={(e)=>{setCoeff2(e.target.value)}}
                    value={coeff2}
                    id={'coeff2_'+target}
            />
          </label>
        )}
        {(
          target && 
          target.length > 0 &&
          func !== 'EXT' &&
          <label className='editor-small' >Power/Sat constant
            <input  type = "text"
                    style={{width: '95%', display: 'flex'}}
                    onChange={(e)=>{setCoeff3(e.target.value)}}
                    value={coeff3}
                    id={'coeff3_'+target}
            />
          </label>
        )}
        {(
          target && 
          target.length > 0 &&
          func === 'EXT' &&
          <label className='editor-small' >Scaling factor
            <input  type = "text"
                    style={{width: '95%', display: 'flex'}}
                    onChange={(e)=>{setScaleFactor(e.target.value)}}
                    value={scaleFactor}
                    id={'scaleFactor_'+target}
            />
          </label>
        )}
        {(
          target && 
          target.length > 0 &&
          func === 'EXT' &&
          <label className='editor-small' >Time series
            <UniversalDropDown style={{padding: '0'}}
              IDs={timeSeries_s} 
              selectText={"Select a Time series"} 
              value={timeSeries}
              onChange={(val)=>{setTimeSeries(val)}} >
            </UniversalDropDown>
          </label>
        )}
      </>
        }
    </>
  )
}